import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useSEO from '../hooks/useSEO';

const SharedPropertiesPage = () => {
  const { idGenerado } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Configuración de la API
  const API_BASE_URL = 'https://zonaclientes.softinm.com/api/inmuebles/consultar_inmuebles_compartidos';
  const NOMBRE_EMPRESA = process.env.REACT_APP_SHARED_PROPERTIES_EMPRESA || process.env.REACT_APP_EMPRESA_NAME || 'jdpropiedadraiz';
  const TOKEN = process.env.REACT_APP_SHARED_PROPERTIES_TOKEN || process.env.REACT_APP_API_TOKEN;

  useEffect(() => {
    const fetchSharedProperties = async () => {
      if (!idGenerado) {
        setError('ID no válido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE_URL}/${NOMBRE_EMPRESA}/${idGenerado}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${TOKEN}`,
            'Content-Type': 'application/json',
          },
        });

        const result = await response.json();

        if (result.exito) {
          setData(result.data);
        } else {
          setError(result.mensaje || 'Error al cargar los inmuebles compartidos');
        }
      } catch (err) {
        setError('Error de conexión. Por favor, intente nuevamente.');
        console.error('Error fetching shared properties:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSharedProperties();
  }, [idGenerado]);

  // SEO dinámico basado en los datos
  useSEO({
    title: data ? `Inmuebles para ${data.nombre} - J&D Propiedad Raíz` : 'Inmuebles Compartidos - J&D Propiedad Raíz',
    description: data 
      ? `Inmuebles seleccionados especialmente para ${data.nombre}. ${data.lsInmuebles?.length || 0} propiedades disponibles.`
      : 'Consulta los inmuebles que hemos seleccionado especialmente para ti.',
    keywords: 'inmuebles compartidos, propiedades seleccionadas, J&D Propiedad Raíz',
    ogTitle: data ? `${data.lsInmuebles?.length || 0} inmuebles seleccionados para ${data.nombre}` : 'Inmuebles Compartidos',
    ogDescription: data 
      ? `Revisa las propiedades que hemos seleccionado especialmente para ${data.nombre}`
      : 'Inmuebles seleccionados por nuestros asesores',
    ogImage: 'https://jdpropiedadraiz.co/logo192.png'
  });

  // Componente de Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-brand-black text-brand-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-gold mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-brand-gold mb-2">Cargando inmuebles...</h2>
          <p className="text-brand-gray">Por favor, espera un momento</p>
        </div>
      </div>
    );
  }

  // Componente de Error
  if (error) {
    return (
      <div className="min-h-screen bg-brand-black text-brand-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-8 mb-6">
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h2 className="text-2xl font-bold text-red-400 mb-2">Error al cargar</h2>
            <p className="text-red-300 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-brand-gold text-brand-black px-6 py-3 rounded-lg font-semibold hover:bg-brand-gold/90 transition-all duration-300"
            >
              Intentar nuevamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Función para formatear precio
  const formatPrice = (price) => {
    if (!price) return 'Precio por consultar';
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Función para formatear área
  const formatArea = (area) => {
    if (!area) return '';
    return `${area} m²`;
  };

  return (
    <div className="min-h-screen bg-brand-black text-brand-white relative overflow-hidden">
      
      {/* Background with animated elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-black via-brand-black to-brand-gold/5 animate-pulse"></div>
        
        {/* Floating logo watermarks */}
        <div className="absolute top-20 left-10 opacity-5 transform rotate-12 animate-bounce">
          <img src="/logo192.png" alt="" className="w-32 h-32 filter brightness-0 invert" />
        </div>
        <div className="absolute top-1/3 right-20 opacity-3 transform -rotate-12 animate-pulse">
          <img src="/logo192.png" alt="" className="w-24 h-24 filter brightness-0 invert" />
        </div>
        <div className="absolute bottom-1/4 left-1/4 opacity-4 transform rotate-45 animate-bounce" style={{animationDelay: '1s'}}>
          <img src="/logo192.png" alt="" className="w-20 h-20 filter brightness-0 invert" />
        </div>
      </div>

      {/* Header Section */}
      <section className="relative pt-32 pb-20 px-6 z-10">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-brand-gold">
              Inmuebles seleccionados para
            </h1>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-brand-white">
              {data?.nombre || 'Cliente'}
            </h2>
            
            {/* Información del cliente */}
            {data && (
              <div className="bg-brand-gold/10 border border-brand-gold/20 rounded-xl p-6 max-w-2xl mx-auto mb-8">
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  {data.telefono && (
                    <div className="flex items-center justify-center md:justify-start">
                      <svg className="w-4 h-4 text-brand-gold mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                      <span className="text-brand-gray">{data.telefono}</span>
                    </div>
                  )}
                  {data.email && (
                    <div className="flex items-center justify-center md:justify-start">
                      <svg className="w-4 h-4 text-brand-gold mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                      <span className="text-brand-gray">{data.email}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <p className="text-xl text-brand-gray">
              Hemos seleccionado <span className="text-brand-gold font-semibold">{data?.lsInmuebles?.length || 0} inmuebles</span> que podrían interesarte
            </p>
          </div>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="py-12 px-6 relative z-10">
        <div className="container mx-auto max-w-7xl">
          {data?.lsInmuebles && data.lsInmuebles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {data.lsInmuebles.map((inmueble, index) => (
                <div 
                  key={inmueble.consecutivo || index}
                  className="bg-brand-gold/5 border border-brand-gold/20 rounded-xl overflow-hidden hover:transform hover:scale-[1.02] transition-all duration-300 hover:shadow-lg hover:shadow-brand-gold/10"
                >
                  {/* Imagen del inmueble */}
                  <div className="relative h-48 bg-brand-gold/10">
                    {inmueble.imagen ? (
                      <img 
                        src={inmueble.imagen} 
                        alt={inmueble.titulo || 'Inmueble'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="absolute inset-0 bg-brand-gold/10 flex items-center justify-center" style={{display: inmueble.imagen ? 'none' : 'flex'}}>
                      <svg className="w-16 h-16 text-brand-gold/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5v4l8-4v4" />
                      </svg>
                    </div>
                    
                    {/* Badge de tipo de operación */}
                    {inmueble.tipoOperacion && (
                      <div className="absolute top-4 left-4 bg-brand-gold text-brand-black px-3 py-1 rounded-full text-sm font-semibold">
                        {inmueble.tipoOperacion}
                      </div>
                    )}
                  </div>

                  {/* Información del inmueble */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-brand-white mb-2">
                      {inmueble.titulo || inmueble.direccion || 'Inmueble disponible'}
                    </h3>
                    
                    {inmueble.direccion && (
                      <p className="text-brand-gray mb-3 flex items-start">
                        <svg className="w-4 h-4 text-brand-gold mr-2 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        {inmueble.direccion}
                      </p>
                    )}

                    {/* Características principales */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {inmueble.habitaciones && (
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-brand-gold mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                          </svg>
                          <span className="text-sm text-brand-gray">{inmueble.habitaciones} hab.</span>
                        </div>
                      )}
                      
                      {inmueble.banos && (
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-brand-gold mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732L14.146 12.8l-1.179 4.456a1 1 0 01-1.934 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732L9.854 7.2l1.179-4.456A1 1 0 0112 2z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-brand-gray">{inmueble.banos} baños</span>
                        </div>
                      )}
                      
                      {inmueble.area && (
                        <div className="flex items-center col-span-2">
                          <svg className="w-4 h-4 text-brand-gold mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15.586 13H14a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-brand-gray">{formatArea(inmueble.area)}</span>
                        </div>
                      )}
                    </div>

                    {/* Precio */}
                    {inmueble.precio && (
                      <div className="mb-4">
                        <p className="text-2xl font-bold text-brand-gold">
                          {formatPrice(inmueble.precio)}
                        </p>
                      </div>
                    )}

                    {/* Botón de contacto */}
                    <div className="flex gap-3">
                      <a
                        href={`https://wa.me/573102390002?text=Hola,%20estoy%20interesado%20en%20el%20inmueble%20${inmueble.consecutivo || 'compartido'}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-brand-gold text-brand-black px-4 py-3 rounded-lg font-semibold hover:bg-brand-gold/90 transition-all duration-300 text-center hover:shadow-lg hover:shadow-brand-gold/25"
                      >
                        Contactar
                      </a>
                      {inmueble.consecutivo && (
                        <a
                          href={`/inmueble/${inmueble.consecutivo}`}
                          className="flex-1 border-2 border-brand-gold text-brand-gold px-4 py-3 rounded-lg font-semibold hover:bg-brand-gold hover:text-brand-black transition-all duration-300 text-center"
                        >
                          Ver más
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <svg className="w-24 h-24 text-brand-gold/50 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m11 0H9m0 0H5m4 0V9a2 2 0 012-2h2a2 2 0 012 2v12M13 7h-2M9 17h.01M15 17h.01" />
              </svg>
              <h3 className="text-2xl font-bold text-brand-gold mb-2">No hay inmuebles disponibles</h3>
              <p className="text-brand-gray">No se encontraron inmuebles para mostrar en este momento.</p>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-6 relative z-10">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="bg-gradient-to-r from-brand-gold/10 to-brand-gold/5 p-12 rounded-2xl border border-brand-gold/20 backdrop-blur-sm relative overflow-hidden">
            <h3 className="text-3xl font-bold mb-6 text-brand-gold">
              ¿Necesitas más información?
            </h3>
            <p className="text-xl text-brand-gray mb-8 leading-relaxed">
              Nuestro equipo de asesores está disponible para ayudarte a encontrar la propiedad perfecta para ti.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href={`https://wa.me/573102390002?text=Hola,%20quiero%20más%20información%20sobre%20los%20inmuebles%20compartidos%20para%20${data?.nombre || 'mi consulta'}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-brand-gold text-brand-black px-8 py-4 rounded-lg font-semibold hover:bg-brand-gold/90 transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center hover:shadow-lg hover:shadow-brand-gold/25"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.515z"/>
                </svg>
                Contactar por WhatsApp
              </a>
              <a 
                href="/" 
                className="border-2 border-brand-gold text-brand-gold px-8 py-4 rounded-lg font-semibold hover:bg-brand-gold hover:text-brand-black transition-all duration-300 inline-block text-center"
              >
                Ir a Inicio
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default SharedPropertiesPage;
